import React from "react";
import { useFormContext } from "react-hook-form";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import Department from "../../../../utilities/data/model/Department";
import {
  Button,
  FlatActionList,
  FlatActionListItem,
  Page,
} from "@towersystems/roam-common-native";
import {
  CreateProductRootParamList,
  EditProductRootParamList,
} from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import colors from "react-native-ui-lib/src/style/colors";
import { Feather } from "@expo/vector-icons";
import { ProductFormValues } from "../../components/ProductForm/types";

export type ProductSelectDepartmentScreenProps = StackScreenProps<
  CreateProductRootParamList & EditProductRootParamList,
  "ProductSelectDepartmentScreen"
>;

export const ProductSelectDepartmentScreen = ({
  navigation,
}: ProductSelectDepartmentScreenProps) => {
  const database = useDatabase();

  const [departments, setDepartments] = React.useState<
    Department[] | undefined
  >();

  const { watch, setValue, getValues } = useFormContext<ProductFormValues>();

  const watchedDepartmentId = watch("departmentId");

  React.useEffect(() => {
    database
      .get<Department>("departments")
      .query()
      .fetch()
      .then((l) => setDepartments(l));
  }, []);

  React.useEffect(() => {
    const existingTax = getValues("taxGroupId");

    if (existingTax) {
      return;
    }

    let d = departments?.find((d) => d._raw.id === watchedDepartmentId);

    if (d && d.preferredTaxGroupId) {
      setValue("taxGroupId", d.preferredTaxGroupId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [watchedDepartmentId]);

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () =>
        !watchedDepartmentId ? null : (
          <Button
            label="Remove Department"
            isAction
            onPress={() => {
              setValue("departmentId", "", {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue("categoryId", "", {
                shouldValidate: true,
                shouldDirty: true,
              });
              navigation.goBack();
            }}
            size="compact"
          ></Button>
        ),
    });
  }, [setValue, watchedDepartmentId]);

  const handleOnSelect = React.useCallback((departmentId: string) => {
    setValue("departmentId", departmentId, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("categoryId", "", {
      shouldValidate: true,
      shouldDirty: true,
    });
    navigation.goBack();
  }, []);

  const items: FlatActionListItem[] = React.useMemo(() => {
    return (
      departments?.map((department) => {
        return {
          key: department.id,
          title: department.name,
          onPress: () => handleOnSelect(department.id),
          disabled: watchedDepartmentId == department.id,
          componentRight: (icon) => {
            return watchedDepartmentId == department.id ? (
              <Feather name="check" size={24} color={colors.primary} />
            ) : (
              icon
            );
          },
        };
      }) || []
    );
  }, [departments, watchedDepartmentId, handleOnSelect]);

  return (
    <Page title="Select Department">
      <FlatActionList items={items} />
    </Page>
  );
};
